// Decoding, encoding and the small conveniences that both browser tools need.
//
// Everything here works on the bytes the visitor picked and never touches the
// network. That is the whole promise of /tools, so nothing in this file may
// grow a fetch of the image itself.

// Feature-detected once per page rather than per file. A browser that cannot
// encode a format answers toBlob with a PNG instead of failing, so asking for
// AVIF and trusting the result would silently hand out a mislabelled file.
const encoderSupport = new Map();

/** The formats offered for output, in the order they are shown. */
export const OUTPUT_FORMATS = [
    { mime: 'image/png',  label: 'PNG',  extension: 'png',  lossy: false, note: 'Lossless, keeps transparency' },
    { mime: 'image/jpeg', label: 'JPEG', extension: 'jpg',  lossy: true,  note: 'Small, no transparency' },
    { mime: 'image/webp', label: 'WebP', extension: 'webp', lossy: true,  note: 'Small and keeps transparency' },
    { mime: 'image/avif', label: 'AVIF', extension: 'avif', lossy: true,  note: 'Smallest, not encodable everywhere' },
];

/**
 * Whether this browser can actually encode `mime`.
 *
 * canvas.toBlob falls back to PNG for anything it does not know, so the answer
 * is whatever type comes back rather than whatever was asked for.
 */
export function canEncode(mime) {
    if (mime === 'image/png') return Promise.resolve(true);

    if (!encoderSupport.has(mime)) {
        encoderSupport.set(mime, new Promise(resolve => {
            const probe = document.createElement('canvas');
            probe.width = 1;
            probe.height = 1;
            try {
                probe.toBlob(blob => resolve(!!blob && blob.type === mime), mime, 0.9);
            } catch (err) {
                resolve(false);
            }
        }));
    }

    return encoderSupport.get(mime);
}

/**
 * Decode a picked file into something drawable.
 *
 * createImageBitmap is the path that gets the rotation right: a photo from a
 * phone carries its orientation in EXIF, and drawing the raw pixels puts it on
 * its side. The <img> fallback is for browsers without the options argument,
 * where the tag applies the orientation itself.
 */
export async function decode(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (err) {
            // Falls through. Either the format is one this browser cannot
            // decode, which the fallback will report the same way, or the
            // options argument is unsupported, which the fallback handles.
        }
    }

    const url = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('This browser cannot decode that image'));
            img.src = url;
        });
    } finally {
        // Revoking immediately is safe: a loaded <img> keeps its own decoded
        // copy, and a failed one has nothing left to read.
        URL.revokeObjectURL(url);
    }
}

/**
 * Whether an image carries any transparency.
 *
 * Checked on a small copy rather than the full picture: a few hundred thousand
 * pixels per file adds up over a batch, and scaling down blends a transparent
 * area into its neighbours rather than losing it, so even a small cut-out still
 * shows up as alpha below 255.
 */
export function hasAlpha(image) {
    const PROBE = 128;
    const { width, height } = sizeOf(image);
    const w = Math.max(1, Math.min(PROBE, width));
    const h = Math.max(1, Math.min(PROBE, height));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);

    const { data } = ctx.getImageData(0, 0, w, h);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
    }
    return false;
}

/** The drawn size of a decoded image, whichever of the two shapes it has. */
export function sizeOf(image) {
    return {
        width: image.width || image.naturalWidth,
        height: image.height || image.naturalHeight,
    };
}

/**
 * The size an image should be drawn at to fit inside `maxWidth` x `maxHeight`.
 *
 * Only ever shrinks. Enlarging a picture to fill a box invents detail that was
 * never there, and someone who asks for at most 1920 wide does not want a
 * 400 pixel logo blown up to it.
 */
export function fitWithin(width, height, maxWidth, maxHeight) {
    const scale = Math.min(maxWidth ? maxWidth / width : 1, maxHeight ? maxHeight / height : 1, 1);
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

/** A canvas holding `image`, drawn at `width` x `height`, optionally over a colour. */
export function draw(image, width, height, background) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
    }

    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
}

/** Encode a canvas, rejecting rather than handing back a null blob. */
export function encode(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            blob => blob ? resolve(blob) : reject(new Error(`Could not encode as ${mime}`)),
            mime,
            // Undefined rather than a number for PNG: passing a quality to a
            // lossless encoder is meaningless, and some browsers take it as a
            // hint to switch to a lossy one.
            quality
        );
    });
}

/** `photo.png` with a new extension, keeping any dots inside the name. */
export function renameExtension(filename, extension) {
    const stem = filename.replace(/\.[^.]+$/, '') || 'image';
    return `${stem}.${extension}`;
}

/** Hand a blob to the visitor as a download. */
export function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // A revoke on the next tick rather than straight away. Chrome starts the
    // download asynchronously and reads the URL after the click returns.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Bytes as something a person reads, matching the wording used elsewhere. */
export function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, power);
    return `${value >= 100 || power === 0 ? Math.round(value) : value.toFixed(1)} ${units[power]}`;
}
